import { apiClient } from './client';
import { Channel, ChannelMember, ChannelRole, ChannelType, CursorPage } from '@/types';

export interface ChannelInvite {
  id: string;
  channelId: string;
  invitedBy: string;
  code: string;
  maxUses: number;
  uses: number;
  expiresAt: string | null;
  createdAt: string;
  channel?: { name: string; displayName: string; type: ChannelType };
}

export interface InviteInfo {
  id: string;
  code: string;
  channel: {
    id: string;
    name: string;
    displayName: string;
    type: ChannelType;
    description: string | null;
    _count: { members: number };
  };
}

export interface JoinRequest {
  id: string;
  channelId: string;
  userId: string;
  message: string | null;
  status: string;
  createdAt: string;
  user: { id: string; username: string; displayName: string; avatarUrl: string | null };
}

export const channelsApi = {
  async listMine() {
    const res = await apiClient.get<Channel[]>('/api/channels/my');
    return res.data;
  },

  async list(params?: { cursor?: string; limit?: number; search?: string }) {
    const res = await apiClient.get<CursorPage<Channel>>('/api/channels', { params });
    return res.data;
  },

  async get(channelId: string) {
    const res = await apiClient.get<Channel>(`/api/channels/${channelId}`);
    return res.data;
  },

  async create(data: {
    name: string;
    displayName: string;
    topic?: string;
    description?: string;
    type?: ChannelType;
    password?: string;
    maxMembers?: number;
    mediaEnabled?: boolean;
    reactionsEnabled?: boolean;
    membersCanPost?: boolean;
    chatStyle?: 'BUBBLE' | 'CLASSIC';
  }) {
    const res = await apiClient.post<Channel>('/api/channels', data);
    return res.data;
  },

  async clearMessages(channelId: string): Promise<{ count: number }> {
    const res = await apiClient.post(`/api/channels/${channelId}/clear-messages`);
    return res.data;
  },

  async setMemberRole(channelId: string, userId: string, role: 'OWNER' | 'ADMIN' | 'MODERATOR' | 'MEMBER' | 'GUEST') {
    const res = await apiClient.put(`/api/channels/${channelId}/members/role`, { userId, role });
    return res.data;
  },

  async update(
    channelId: string,
    data: {
      displayName?: string; topic?: string | null; description?: string | null;
      maxMembers?: number; mediaEnabled?: boolean; reactionsEnabled?: boolean;
      membersCanPost?: boolean;
      // Phase 13 — workspace OWNER может пометить свой канал auto-join для новых
      // юзеров с subdomain. Глобальный isDefault — только SUPER_ADMIN.
      isDefault?: boolean;
      chatStyle?: 'BUBBLE' | 'CLASSIC';
    },
  ) {
    const res = await apiClient.put<Channel>(`/api/channels/${channelId}`, data);
    return res.data;
  },

  async delete(channelId: string) {
    await apiClient.delete(`/api/channels/${channelId}`);
  },

  // ── v5.2 Moderation pack ────────────────────────────────────────────────
  // ADMIN+ only. getPublicProfile отдаёт только client-visible isAdult +
  // slowModeSeconds; а здесь полный набор (banned-words/patterns).
  async getModeration(channelId: string): Promise<ChannelModeration> {
    const res = await apiClient.get<ChannelModeration>(
      `/api/channels/${channelId}/moderation`,
    );
    return res.data;
  },

  async patchModeration(
    channelId: string,
    patch: Partial<Omit<ChannelModeration, 'id'>>,
  ): Promise<ChannelModeration> {
    const res = await apiClient.patch<ChannelModeration>(
      `/api/channels/${channelId}/moderation`,
      patch,
    );
    return res.data;
  },

  async join(channelId: string, password?: string) {
    const res = await apiClient.post<ChannelMember>(`/api/channels/${channelId}/join`, { password });
    return res.data;
  },

  async leave(channelId: string) {
    await apiClient.post(`/api/channels/${channelId}/leave`);
  },

  async getMembers(channelId: string, params?: { cursor?: string; limit?: number }) {
    const res = await apiClient.get<CursorPage<ChannelMember>>(
      `/api/channels/${channelId}/members`,
      { params },
    );
    return res.data;
  },

  async kick(channelId: string, userId: string, reason?: string) {
    await apiClient.post(`/api/channels/${channelId}/kick`, { userId, reason });
  },

  async ban(channelId: string, userId: string, reason?: string, expiresAt?: string) {
    await apiClient.post(`/api/channels/${channelId}/ban`, { userId, reason, expiresAt });
  },

  async unban(channelId: string, userId: string) {
    await apiClient.post(`/api/channels/${channelId}/unban`, { userId });
  },

  async mute(channelId: string, userId: string, durationMs: number, reason?: string) {
    await apiClient.post(`/api/channels/${channelId}/mute`, { userId, durationMs, reason });
  },

  async setRole(channelId: string, userId: string, role: ChannelRole) {
    const res = await apiClient.put<ChannelMember>(`/api/channels/${channelId}/members/role`, {
      userId,
      role,
    });
    return res.data;
  },

  // ─── Invites ────────────────────────────────────────────────────────

  async createInvite(channelId: string, opts?: { maxUses?: number; expiresInHours?: number }) {
    const res = await apiClient.post<ChannelInvite>(`/api/channels/${channelId}/invites`, opts ?? {});
    return res.data;
  },

  async listInvites(channelId: string) {
    const res = await apiClient.get<ChannelInvite[]>(`/api/channels/${channelId}/invites`);
    return res.data;
  },

  async deleteInvite(inviteId: string) {
    await apiClient.delete(`/api/channels/invites/${inviteId}`);
  },

  async getInviteByCode(code: string) {
    const res = await apiClient.get<InviteInfo>(`/api/channels/invite/${code}`);
    return res.data;
  },

  async redeemInvite(code: string, message?: string) {
    const res = await apiClient.post<{ status: string; channelId: string }>(`/api/channels/invite/${code}/join`, { message });
    return res.data;
  },

  // ─── Join Requests ──────────────────────────────────────────────────

  async listJoinRequests(channelId: string) {
    const res = await apiClient.get<JoinRequest[]>(`/api/channels/${channelId}/join-requests`);
    return res.data;
  },

  async approveJoinRequest(requestId: string) {
    const res = await apiClient.post<{ channelId: string; userId: string }>(`/api/channels/join-requests/${requestId}/approve`);
    return res.data;
  },

  async rejectJoinRequest(requestId: string) {
    await apiClient.post(`/api/channels/join-requests/${requestId}/reject`);
  },

  async toggleNotifications(channelId: string) {
    const res = await apiClient.put<{ notifyEnabled: boolean }>(`/api/channels/${channelId}/notifications`);
    return res.data;
  },

  async uploadAvatar(channelId: string, file: File) {
    const formData = new FormData();
    formData.append('avatar', file);
    const res = await apiClient.post<{ avatarUrl: string }>(`/api/channels/${channelId}/avatar`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
  },

  async getProfile(channelId: string) {
    const res = await apiClient.get(`/api/channels/${channelId}/profile`);
    return res.data;
  },

  async requestJoin(channelId: string, message?: string) {
    const res = await apiClient.post<{ status: string; channelId: string }>(`/api/channels/${channelId}/request-join`, { message });
    return res.data;
  },
};

// v5.2 — Moderation pack types. Mirror backend Channel fields.
export type AutomodAction = 'DELETE' | 'TIMEOUT' | 'WARN';

export interface ChannelModeration {
  id: string;
  isAdult: boolean;
  slowModeSeconds: number;
  automodBannedWords: string[];
  automodPatterns: string[];
  automodAction: AutomodAction;
  automodTimeoutSec: number;
}

export const AUTOMOD_ACTION_LABELS: Record<AutomodAction, string> = {
  DELETE: 'Удалить',
  TIMEOUT: 'Удалить + mute',
  WARN: 'Только лог',
};
